import {useNavigateToNewThread} from '@github-ui/copilot-chat/hooks/use-navigate-to-new-thread'
import {useChatStateValues} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useCallback, useEffect, useRef, useState} from 'react'

/**
 * Minimum number of messages in a thread before the "start a new chat for a new topic" nudge is
 * eligible to appear. Combined with the staleness gate, this targets deep threads the user is
 * returning to rather than short or in-flight conversations.
 */
export const NEW_TOPIC_NUDGE_MESSAGE_THRESHOLD = 10

/**
 * How long a thread must have gone untouched (no updates) before the nudge is eligible on it. A
 * returning-after-a-gap thread is where a topic switch — and therefore the nudge — is most relevant.
 */
export const NEW_TOPIC_NUDGE_STALE_MS = 12 * 60 * 60 * 1000 // 12 hours

/**
 * How long the nudge stays suppressed for the whole user after they dismiss it. Once this passes it
 * can appear again on any qualifying thread.
 */
export const NEW_TOPIC_NUDGE_DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000 // 1 week

export interface UseNewTopicNudgeResult {
  /** Whether the nudge banner should render right now. */
  show: boolean
  /** Dismiss the nudge and suppress it for the whole user for a week. */
  dismiss: () => void
  /** Start a fresh chat. */
  startNewChat: () => void
  /**
   * Record that the user opened the "Learn more" docs. Emits telemetry only — it does not suppress
   * the nudge, so we can measure whether reading the rationale correlates with users learning the
   * behavior (e.g. later starting new chats on their own).
   */
  explain: () => void
}

/**
 * Decides whether to surface the long-thread "new topic" nudge and owns the dismiss / start-new-chat
 * side effects. Policy:
 * - only on a real (already-created) thread that has more than
 *   {@link NEW_TOPIC_NUDGE_MESSAGE_THRESHOLD} messages and has been untouched for at least
 *   {@link NEW_TOPIC_NUDGE_STALE_MS} (returning to a stale, deep thread), and
 * - once dismissed, suppressed for the whole user for {@link NEW_TOPIC_NUDGE_DISMISS_COOLDOWN_MS};
 *   after that it can appear again on any qualifying thread.
 */
export function useNewTopicNudge(): UseNewTopicNudgeResult {
  const {messages, selectedThreadID, mode} = useChatStateValues('messages', 'selectedThreadID', 'mode')
  const navigateToNewThread = useNavigateToNewThread()

  // Read the persisted dismissal cooldown once on mount; keep a reactive copy so dismissing hides
  // the banner immediately without needing a re-mount.
  const [dismissedUntil, setDismissedUntil] = useState(() => copilotLocalStorage.getNewTopicNudgeDismissedUntil())
  // Capture "now" once at mount. Staleness and the dismissal cooldown are evaluated relative to when
  // the thread was opened; this also keeps the impure `Date.now()` call out of the render body.
  const [now] = useState(() => Date.now())

  const dismiss = useCallback(() => {
    sendEvent('dotcom_chat.activate', {target: 'NEW_TOPIC_NUDGE', action: 'dismiss', mode})
    const until = Date.now() + NEW_TOPIC_NUDGE_DISMISS_COOLDOWN_MS
    copilotLocalStorage.setNewTopicNudgeDismissedUntil(until)
    setDismissedUntil(until)
  }, [mode])

  const startNewChat = useCallback(() => {
    sendEvent('dotcom_chat.activate', {target: 'NEW_TOPIC_NUDGE', action: 'new_chat', mode})
    void navigateToNewThread()
  }, [navigateToNewThread, mode])

  const explain = useCallback(() => {
    sendEvent('dotcom_chat.activate', {target: 'NEW_TOPIC_NUDGE', action: 'explain', mode})
  }, [mode])

  // Staleness is measured from the most recent message's timestamp, not the thread's `updatedAt`.
  // The messages array updates reactively as the user chats, so sending a message (which sets the
  // new message's createdAt to ~now) immediately makes the thread "not stale" — sending IS touching
  // the thread. This prevents the nudge from appearing mid-conversation while the user is actively
  // engaged (e.g. returning to a short thread and chatting past the message threshold).
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : undefined
  const lastActivityMs = lastMessage ? new Date(lastMessage.createdAt).getTime() : 0
  const threadIsStale = lastActivityMs > 0 && now - lastActivityMs >= NEW_TOPIC_NUDGE_STALE_MS

  const show =
    now >= dismissedUntil &&
    Boolean(selectedThreadID) &&
    messages.length > NEW_TOPIC_NUDGE_MESSAGE_THRESHOLD &&
    threadIsStale

  // Emit a one impression event per thread the nudge is shown on. This is the denominator for the
  // dismiss / new_chat / explain action rates — without it those events can't be turned into rates.
  // Keyed on the thread so a genuinely new qualifying thread re-emits while re-renders within the
  // same thread do not.
  //
  // This is a genuine effect, not a disguised event handler: "the banner became visible" is a
  // render-driven visibility transition (derived from state) with no corresponding user event to
  // hook, and it synchronizes with an external system (analytics). eslint-disable is intentional.
  const shownForThreadRef = useRef<string | null>(null)
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- see comment above: impression tracking on a render-driven visibility change is a genuine effect, not an event handler
    if (!show || !selectedThreadID) return
    if (shownForThreadRef.current === selectedThreadID) return
    shownForThreadRef.current = selectedThreadID
    sendEvent('dotcom_chat.activate', {target: 'NEW_TOPIC_NUDGE', action: 'shown', mode})
  }, [show, selectedThreadID, mode])

  return {show, dismiss, startNewChat, explain}
}
